"use client";

import { useMemo, useState } from "react";
import {
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CompactTextArea,
  CopyList,
  DraftPanel,
  FieldGroup,
  FormHeader,
  optionsOf,
} from "@/components/tools/academic-correspondence/kit";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  countPlaceholders,
  draftToPlain,
} from "@/lib/tool-kit/correspondence/compose";
import {
  ACKNOWLEDGEMENTS_BANK,
  type AcknowledgementsInput,
  buildAcknowledgements,
  type CommitteeTerm,
  type DedicationStyle,
  type DocumentWord,
  type HeadingSpelling,
  type SupervisorTerm,
} from "@/lib/tool-kit/correspondence/thesis-acknowledgements";

/** English strings. A translated content file overrides these through `ui`. */
export const THESIS_ACKNOWLEDGEMENTS_GENERATOR_STRINGS = {
  formHeading: "Who you are thanking",
  tryExample: "Try an example",
  clear: "Clear",
  styleHeading: "Style",
  documentWordLabel: "What your university calls it",
  documentWordOptions: {
    thesis: "Thesis",
    dissertation: "Dissertation",
  },
  spellingLabel: "Heading spelling",
  spellingOptions: {
    uk: "Acknowledgements (British and Commonwealth)",
    us: "Acknowledgments (American)",
  },
  toneLabel: "Tone",
  toneOptions: {
    formal: "Formal",
    warm: "Warm",
  },
  phrasingLabel: "Phrasing",
  phrasingOptions: {
    v1: "Version 1",
    v2: "Version 2",
    v3: "Version 3",
  },
  supervisionHeading: "Supervision and examination",
  supervisorTermLabel: "Your university's term",
  supervisorTermOptions: {
    supervisor: "Supervisor",
    advisor: "Advisor",
  },
  supervisorsLabel:
    "Supervisors or advisors, one per line, as their names should appear",
  supervisorsPlaceholder: "Professor Ana Lima\nDr. Kwame Mensah",
  supervisorThanksLabel: "What you are thanking them for (optional)",
  supervisorThanksPlaceholder:
    "their patience, their sharp questions, and the hours spent on my drafts",
  thanksHint:
    "Finish the sentence “Thank you for…”. Leave blank for a conventional phrase.",
  committeeTermLabel: "Committee or examiners",
  committeeTermOptions: {
    committee: "Committee members",
    examiners: "Examiners",
    panel: "Advisory panel",
  },
  committeeLabel: "Their names, one per line (optional)",
  committeePlaceholder: "Professor Ingrid Solberg\nDr. Rahul Mehta",
  committeeThanksLabel: "What you are thanking them for (optional)",
  committeeThanksPlaceholder:
    "a stimulating viva and their careful corrections",
  supportHeading: "Funding and support",
  fundersLabel: "Funders and scholarships, one per line (optional)",
  fundersPlaceholder: "the Regional Water Research Fund (grant WRF-2023-118)",
  fundersHint:
    "Many funders require set wording and a grant number. Check yours and copy it exactly.",
  institutionsLabel:
    "Institutions, labs, and services, one per line (optional)",
  institutionsPlaceholder:
    "the Coastal Hydrology Lab\nthe staff of the university library",
  peopleHeading: "Participants, colleagues, family",
  participantsLabel: "Research participants (optional)",
  participantsPlaceholder: "the 32 farmers who shared their time and knowledge",
  participantsHint:
    "Describe them as a group. Never name participants or give details that could identify them.",
  colleaguesLabel: "Colleagues and peers, one per line (optional)",
  colleaguesPlaceholder:
    "my colleagues in the Hydrology Group, especially Tomás and Mei",
  familyLabel: "Family and friends, one per line (optional)",
  familyPlaceholder: "my parents, Rosa and Daniel\nmy partner, Sam",
  dedicationHeading: "Dedication (optional)",
  dedicationLabel: "Dedicate it to",
  dedicationPlaceholder: "my grandmother, Amara Diallo (1938–2021)",
  dedicationStyleLabel: "Wording",
  dedicationStyleOptions: {
    to: "To …",
    for: "For …",
    memory: "In memory of …",
  },
  dedicationOutputHeading:
    "Dedication — on its own page, before the acknowledgements",
  draftHeading: "Your acknowledgements",
  copy: "Copy",
  copyPlain: "Copy plain text",
  copied: "Copied!",
  download: "Download .txt",
  downloadFilename: "acknowledgements.txt",
  placeholdersLeft:
    "one:{count} placeholder left to fill — shown in [brackets]|other:{count} placeholders left to fill — shown in [brackets]",
  noPlaceholders:
    "No placeholders left. Check every name and title against how each person writes it.",
  example: {
    supervisors: "Professor Ana Lima\nDr. Kwame Mensah",
    supervisorThanks:
      "their patience, their sharp questions, and the many hours they spent reading drafts",
    committee: "Professor Ingrid Solberg\nDr. Rahul Mehta",
    committeeThanks: "a stimulating viva and their careful corrections",
    funders: "the Regional Water Research Fund (grant WRF-2023-118)",
    institutions:
      "the Coastal Hydrology Lab\nthe staff of the university library",
    participants:
      "the 32 farmers who welcomed me onto their land and shared their knowledge",
    colleagues:
      "my colleagues in the Hydrology Group, especially Tomás and Mei",
    family: "my parents, Rosa and Daniel\nmy partner, Sam",
    dedication: "my grandmother, Amara Diallo (1938–2021)",
  },
  ...ACKNOWLEDGEMENTS_BANK,
} as const;

type Strings = typeof THESIS_ACKNOWLEDGEMENTS_GENERATOR_STRINGS;

const EMPTY: AcknowledgementsInput = {
  documentWord: "thesis",
  spelling: "uk",
  tone: "formal",
  phrasing: "v1",
  supervisorTerm: "supervisor",
  supervisors: "",
  supervisorThanks: "",
  committeeTerm: "committee",
  committee: "",
  committeeThanks: "",
  funders: "",
  institutions: "",
  participants: "",
  colleagues: "",
  family: "",
  dedication: "",
  dedicationStyle: "to",
};

type Update = <K extends keyof AcknowledgementsInput>(
  key: K,
  value: AcknowledgementsInput[K]
) => void;

function StyleFields({
  form,
  update,
  strings,
}: {
  form: AcknowledgementsInput;
  update: Update;
  strings: Strings;
}) {
  return (
    <FieldGroup title={strings.styleHeading}>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="tak-document-word"
          label={strings.documentWordLabel}
          onChange={(value: DocumentWord) => update("documentWord", value)}
          options={optionsOf(
            ["thesis", "dissertation"] as const,
            strings.documentWordOptions
          )}
          value={form.documentWord}
        />
        <SelectField
          id="tak-spelling"
          label={strings.spellingLabel}
          onChange={(value: HeadingSpelling) => update("spelling", value)}
          options={optionsOf(["uk", "us"] as const, strings.spellingOptions)}
          value={form.spelling}
        />
      </div>
      <PillGroup
        label={strings.toneLabel}
        name="tak-tone"
        onChange={(value) => update("tone", value)}
        options={optionsOf(["formal", "warm"] as const, strings.toneOptions)}
        value={form.tone}
      />
      <PillGroup
        label={strings.phrasingLabel}
        name="tak-phrasing"
        onChange={(value) => update("phrasing", value)}
        options={optionsOf(
          ["v1", "v2", "v3"] as const,
          strings.phrasingOptions
        )}
        value={form.phrasing}
      />
    </FieldGroup>
  );
}

function SupervisionFields({
  form,
  update,
  strings,
}: {
  form: AcknowledgementsInput;
  update: Update;
  strings: Strings;
}) {
  return (
    <FieldGroup title={strings.supervisionHeading}>
      <SelectField
        id="tak-supervisor-term"
        label={strings.supervisorTermLabel}
        onChange={(value: SupervisorTerm) => update("supervisorTerm", value)}
        options={optionsOf(
          ["supervisor", "advisor"] as const,
          strings.supervisorTermOptions
        )}
        value={form.supervisorTerm}
      />
      <CompactTextArea
        id="tak-supervisors"
        label={strings.supervisorsLabel}
        onChange={(value) => update("supervisors", value)}
        placeholder={strings.supervisorsPlaceholder}
        rows={2}
        value={form.supervisors}
      />
      <TextField
        hint={strings.thanksHint}
        id="tak-supervisor-thanks"
        label={strings.supervisorThanksLabel}
        onChange={(value) => update("supervisorThanks", value)}
        placeholder={strings.supervisorThanksPlaceholder}
        value={form.supervisorThanks}
      />
      <SelectField
        id="tak-committee-term"
        label={strings.committeeTermLabel}
        onChange={(value: CommitteeTerm) => update("committeeTerm", value)}
        options={optionsOf(
          ["committee", "examiners", "panel"] as const,
          strings.committeeTermOptions
        )}
        value={form.committeeTerm}
      />
      <CompactTextArea
        id="tak-committee"
        label={strings.committeeLabel}
        onChange={(value) => update("committee", value)}
        placeholder={strings.committeePlaceholder}
        rows={2}
        value={form.committee}
      />
      <TextField
        id="tak-committee-thanks"
        label={strings.committeeThanksLabel}
        onChange={(value) => update("committeeThanks", value)}
        placeholder={strings.committeeThanksPlaceholder}
        value={form.committeeThanks}
      />
    </FieldGroup>
  );
}

function PeopleFields({
  form,
  update,
  strings,
}: {
  form: AcknowledgementsInput;
  update: Update;
  strings: Strings;
}) {
  return (
    <>
      <FieldGroup title={strings.supportHeading}>
        <CompactTextArea
          hint={strings.fundersHint}
          id="tak-funders"
          label={strings.fundersLabel}
          onChange={(value) => update("funders", value)}
          placeholder={strings.fundersPlaceholder}
          rows={2}
          value={form.funders}
        />
        <CompactTextArea
          id="tak-institutions"
          label={strings.institutionsLabel}
          onChange={(value) => update("institutions", value)}
          placeholder={strings.institutionsPlaceholder}
          rows={2}
          value={form.institutions}
        />
      </FieldGroup>
      <FieldGroup title={strings.peopleHeading}>
        <TextField
          hint={strings.participantsHint}
          id="tak-participants"
          label={strings.participantsLabel}
          onChange={(value) => update("participants", value)}
          placeholder={strings.participantsPlaceholder}
          value={form.participants}
        />
        <CompactTextArea
          id="tak-colleagues"
          label={strings.colleaguesLabel}
          onChange={(value) => update("colleagues", value)}
          placeholder={strings.colleaguesPlaceholder}
          rows={2}
          value={form.colleagues}
        />
        <CompactTextArea
          id="tak-family"
          label={strings.familyLabel}
          onChange={(value) => update("family", value)}
          placeholder={strings.familyPlaceholder}
          rows={2}
          value={form.family}
        />
      </FieldGroup>
      <FieldGroup title={strings.dedicationHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="tak-dedication"
            label={strings.dedicationLabel}
            onChange={(value) => update("dedication", value)}
            placeholder={strings.dedicationPlaceholder}
            value={form.dedication}
          />
          <SelectField
            id="tak-dedication-style"
            label={strings.dedicationStyleLabel}
            onChange={(value: DedicationStyle) =>
              update("dedicationStyle", value)
            }
            options={optionsOf(
              ["to", "for", "memory"] as const,
              strings.dedicationStyleOptions
            )}
            value={form.dedicationStyle}
          />
        </div>
      </FieldGroup>
    </>
  );
}

export default function ThesisAcknowledgementsGeneratorWidget() {
  const strings = useToolUi(THESIS_ACKNOWLEDGEMENTS_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [form, setForm] = useState<AcknowledgementsInput>(EMPTY);

  const update: Update = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const draft = useMemo(
    () => buildAcknowledgements(form, strings, locale),
    [form, strings, locale]
  );
  const blanks = countPlaceholders(draft.paragraphs);
  const status =
    blanks > 0
      ? tPlural(strings.placeholdersLeft, locale, blanks, {
          count: fmt(blanks),
        })
      : strings.noPlaceholders;
  const body = draftToPlain(draft.paragraphs);

  return (
    <>
      <section className="space-y-5">
        <FormHeader
          clearLabel={strings.clear}
          exampleLabel={strings.tryExample}
          heading={strings.formHeading}
          onClear={() => setForm(EMPTY)}
          onExample={() =>
            setForm({
              ...EMPTY,
              ...strings.example,
              committeeTerm: "examiners",
              dedicationStyle: "memory",
            })
          }
        />
        <StyleFields form={form} strings={strings} update={update} />
        <SupervisionFields form={form} strings={strings} update={update} />
        <PeopleFields form={form} strings={strings} update={update} />
      </section>

      <DraftPanel
        download={
          draft.dedication === "" ? body : `${draft.dedication}\n\n\n${body}`
        }
        filename={strings.downloadFilename}
        heading={strings.draftHeading}
        intro={
          <CopyList
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            heading={strings.dedicationOutputHeading}
            items={draft.dedication === "" ? [] : [draft.dedication]}
          />
        }
        paragraphs={draft.paragraphs}
        status={status}
        strings={strings}
      />
    </>
  );
}
